import { useState } from 'react';
import api from './api';
import { GUIDE_STEPS, GOOD_TO_KNOW, PhotoAndPassportExamples } from './GuideContent';

const RED = 'var(--accent)';

// ─────────────────────────────────────────────────────────────────────────────
// GuidePage — обов'язковий повноекранний гейт з інструкцією по заповненню
// особистих даних. Показується при першому логіні (і всім вже активним
// фрілансерам, які ще не підтверджували — без винятку) до онбординг-візарда.
// Підтвердження ("Got it") зберігається на бекенді (freelancers.guide_ack_at).
// ─────────────────────────────────────────────────────────────────────────────
export default function GuidePage({ onDone }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleGotIt = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.freelancers.ackGuide();
      onDone(res.guideAckAt);
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif', display: 'flex', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: '720px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ color: RED, fontWeight: 'bold', fontSize: '12px', letterSpacing: '1px', marginBottom: '8px' }}>
            SCULL CREW — CREW PORTAL
          </div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '24px', margin: '0 0 8px' }}>Guide to Completing Your Personal Data</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>
            Please read this before filling in your profile — it explains what each step needs and why.
          </p>
          <a href="/docs/Freelancer_Data_Entry_Guide.pdf" target="_blank" rel="noreferrer"
            style={{ color: RED, fontSize: '12px', display: 'inline-block', marginTop: '10px' }}>
            📄 Download as PDF
          </a>
        </div>

        <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '20px' }}>
          <p style={{ color: 'var(--text-body)', fontSize: '13px', lineHeight: 1.6, margin: '0 0 8px' }}>
            When you complete your profile, you'll go through six steps. This information is required so that
            Scull Crew can assign you to projects, arrange your travel, process your payments correctly, and
            contact you or your emergency contact if needed.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: 1.6, margin: 0 }}>
            Fields marked <span style={{ color: RED, fontWeight: 'bold' }}>Required</span> must be completed before
            you can move on. Optional fields help us match you to the right projects but can be left blank.
          </p>
        </div>

        {GUIDE_STEPS.map((step, i) => (
          <div key={step.title} style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
            <h2 style={{ color: RED, fontSize: '15px', margin: '0 0 10px' }}>Step {i + 1} — {step.title}</h2>
            {step.intro && <p style={{ color: 'var(--text-muted)', fontSize: '12.5px', lineHeight: 1.6, margin: '0 0 12px' }}>{step.intro}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {step.fields.map(f => (
                <div key={f.label} style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
                  <span style={{
                    fontSize: '10px', fontWeight: 'bold', padding: '2px 7px', borderRadius: '4px', flexShrink: 0,
                    color: f.required ? RED : 'var(--text-faint)',
                    background: f.required ? 'rgba(230,57,70,0.12)' : 'var(--bg-card)',
                    border: `1px solid ${f.required ? 'rgba(230,57,70,0.4)' : 'var(--border)'}`,
                  }}>
                    {f.required ? 'REQUIRED' : 'OPTIONAL'}
                  </span>
                  <div>
                    <span style={{ color: 'var(--text-body)', fontSize: '13px', fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: 'var(--text-faint)', fontSize: '12.5px' }}> — {f.note}</span>
                  </div>
                </div>
              ))}
            </div>
            {step.visualExamples && <PhotoAndPassportExamples />}
            {step.warning && (
              <p style={{ color: 'var(--warning-text)', fontSize: '12px', marginTop: '14px', marginBottom: 0 }}>{step.warning}</p>
            )}
          </div>
        ))}

        <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '14px', margin: '0 0 10px' }}>Good to know</h2>
          <ul style={{ color: 'var(--text-muted)', fontSize: '12.5px', lineHeight: 1.8, margin: 0, paddingLeft: '18px' }}>
            {GOOD_TO_KNOW.map(t => <li key={t}>{t}</li>)}
          </ul>
        </div>

        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ textAlign: 'center' }}>
          <button
            type="button" onClick={handleGotIt} disabled={saving}
            style={{
              background: saving ? 'var(--text-dim)' : RED, color: 'var(--text-primary)', border: 'none', borderRadius: '8px',
              padding: '14px 48px', fontSize: '15px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            }}>
            {saving ? 'Saving…' : "Got it — I've read this"}
          </button>
        </div>
      </div>
    </div>
  );
}
